import React, {useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Track from './Track'
import Slider from './Slider'
import * as valueTransformer from './valueTransformer'
import {isObject, distanceTo, isDefined, length} from './utils'
import {DOWN_ARROW, LEFT_ARROW, RIGHT_ARROW, UP_ARROW} from './KeyCodes'
import Label from './Label'

const StyledInputRange = styled.div`
  height: 1rem;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`
/**
 * Input Range component
 * Based on react-input-range https://github.com/davidchin/react-input-range
 */
const InputRange = props => {
  const {
    minValue,
    maxValue,
    allowSameValues,
    formatLabel,
    disabled,
    step,
    onChange,
    onChangeStart,
    onChangeComplete,
    draggableTrack,
    value,
    showSliderLabel
  } = props

  useEffect(() => {
    return () => {
      removeDocumentMouseUpListener()
      removeDocumentTouchEndListener()
    }
  }, [])

  const isMultiValue = isObject(value)

  const getKeys = () => {
    if (isMultiValue) {
      return ['min', 'max']
    }

    return ['max']
  }

  let startValue = useRef(null)

  let node = useRef(null)

  let trackNode = useRef(null)

  let isSliderDragging = useRef(false)

  let lastKeyMoved = useRef(null)

  const addDocumentMouseUpListener = () => {
    removeDocumentMouseUpListener()
    node.current.ownerDocument.addEventListener('mouseup', handleMouseUp)
  }

  const addDocumentTouchEndListener = () => {
    removeDocumentTouchEndListener()
    node.current.ownerDocument.addEventListener('touchend', handleTouchEnd)
  }

  const removeDocumentMouseUpListener = () => {
    node.current.ownerDocument.removeEventListener('mouseup', handleMouseUp)
  }

  const removeDocumentTouchEndListener = () => {
    node.current.ownerDocument.removeEventListener('touchend', handleTouchEnd)
  }

  const handleMouseUp = event => {
    handleInteractionEnd(event)
    removeDocumentMouseUpListener()
  }

  const handleTouchEnd = event => {
    handleInteractionEnd(event)
    removeDocumentTouchEndListener()
  }

  const getTrackClientRect = () => {
    return trackNode.current.getClientRect()
  }

  const updatePosition = (key, position) => {
    const values = valueTransformer.getValueFromProps(props, isMultiValue)
    const positions = valueTransformer.getPositionsFromValues(
      values,
      minValue,
      maxValue,
      getTrackClientRect()
    )

    positions[key] = position
    lastKeyMoved.current = key

    updatePositions(positions)
  }

  const updatePositions = positions => {
    const values = {
      min: valueTransformer.getValueFromPosition(
        positions.min,
        minValue,
        maxValue,
        getTrackClientRect()
      ),
      max: valueTransformer.getValueFromPosition(
        positions.max,
        minValue,
        maxValue,
        getTrackClientRect()
      )
    }

    const transformedValues = {
      min: valueTransformer.getStepValueFromValue(values.min, step),
      max: valueTransformer.getStepValueFromValue(values.max, step)
    }

    updateValues(transformedValues)
  }

  const hasStepDifference = values => {
    const currentValues = valueTransformer.getValueFromProps(
      props,
      isMultiValue
    )

    return (
      length(values.min, currentValues.min) >= step ||
      length(values.max, currentValues.max) >= step
    )
  }

  const isWithinRange = values => {
    if (isMultiValue) {
      return values.min >= minValue && values.max <= maxValue && allowSameValues
        ? values.min <= values.max
        : values.min < values.max
    }

    return values.max >= minValue && values.max <= maxValue
  }

  const shouldUpdate = values => {
    return isWithinRange(values) && hasStepDifference(values)
  }

  const updateValues = values => {
    if (!shouldUpdate(values)) {
      return
    }
    onChange(isMultiValue ? values : values.max)
  }

  const handleSliderDrag = (evt, key) => {
    if (disabled) {
      return
    }

    const position = valueTransformer.getPositionFromEvent(
      evt,
      getTrackClientRect()
    )
    isSliderDragging.current = true
    requestAnimationFrame(() => updatePosition(key, position))
  }

  const incrementValue = key => {
    const values = valueTransformer.getValueFromProps(props, isMultiValue)
    const value = values[key] + step

    updateValue(key, value)
  }

  const decrementValue = key => {
    const values = valueTransformer.getValueFromProps(props, isMultiValue)
    const value = values[key] - step

    updateValue(key, value)
  }

  const updateValue = (key, value) => {
    const values = valueTransformer.getValueFromProps(props, isMultiValue)

    values[key] = value

    updateValues(values)
  }

  const handleSliderKeyDown = (evt, key) => {
    if (disabled) {
      return
    }

    switch (evt.keyCode) {
      case LEFT_ARROW:
      case DOWN_ARROW:
        evt.preventDefault()
        decrementValue(key)
        break

      case RIGHT_ARROW:
      case UP_ARROW:
        evt.preventDefault()
        incrementValue(key)
        break

      default:
        break
    }
  }

  const values = valueTransformer.getValueFromProps(props, isMultiValue)
  const percentages = valueTransformer.getPercentagesFromValues(
    values,
    minValue,
    maxValue
  )

  const renderSliders = () => {
    const keys =
      allowSameValues && lastKeyMoved.current === 'min'
        ? getKeys().reverse()
        : getKeys()

    return keys.map(key => {
      const value = values[key]
      const percentage = percentages[key]

      let {maxValue, minValue} = props

      if (key === 'min') {
        maxValue = values.max
      } else {
        minValue = values.min
      }

      return (
        <Slider
          formatLabel={formatLabel}
          key={key}
          maxValue={maxValue}
          minValue={minValue}
          onSliderDrag={handleSliderDrag}
          onSliderKeyDown={handleSliderKeyDown}
          percentage={percentage}
          type={key}
          value={value}
          showSliderLabel={showSliderLabel}
        />
      )
    })
  }

  const getKeyByPosition = position => {
    const values = valueTransformer.getValueFromProps(props, isMultiValue)
    const positions = valueTransformer.getPositionsFromValues(
      values,
      minValue,
      maxValue,
      getTrackClientRect()
    )

    if (isMultiValue) {
      const distanceToMin = distanceTo(position, positions.min)
      const distanceToMax = distanceTo(position, positions.max)

      if (distanceToMin < distanceToMax) {
        return 'min'
      }
    }

    return 'max'
  }

  const handleTrackMouseDown = (event, position) => {
    if (disabled) {
      return
    }

    const {
      maxValue,
      minValue,
      value: {max, min}
    } = props

    event.preventDefault()

    const value = valueTransformer.getValueFromPosition(
      position,
      minValue,
      maxValue,
      getTrackClientRect()
    )
    const stepValue = valueTransformer.getStepValueFromValue(value, step)

    if (!draggableTrack || stepValue > max || stepValue < min) {
      updatePosition(getKeyByPosition(position), position)
    }
  }

  const handleKeyDown = event => {
    handleInteractionStart(event)
  }

  const handleKeyUp = event => {
    handleInteractionEnd(event)
  }

  const handleMouseDown = event => {
    handleInteractionStart(event)
    addDocumentMouseUpListener()
  }

  const handleTouchStart = event => {
    handleInteractionStart(event)
    addDocumentTouchEndListener()
  }

  const handleInteractionStart = () => {
    if (onChangeStart) {
      onChangeStart(value)
    }

    if (onChangeComplete && !isDefined(startValue.current)) {
      startValue.current = value
    }
  }

  const handleInteractionEnd = () => {
    if (isSliderDragging.current) {
      isSliderDragging.current = false
    }

    if (!onChangeComplete || !isDefined(startValue.current)) {
      return
    }

    if (startValue.current !== value) {
      onChangeComplete(value)
    }

    startValue.current = null
  }

  const handleTrackDrag = (event, prevEvent) => {
    if (disabled || !draggableTrack || isSliderDragging.current) {
      return
    }

    const {
      maxValue,
      minValue,
      value: {max, min}
    } = props

    const position = valueTransformer.getPositionFromEvent(
      event,
      getTrackClientRect()
    )
    const value = valueTransformer.getValueFromPosition(
      position,
      minValue,
      maxValue,
      getTrackClientRect()
    )
    const stepValue = valueTransformer.getStepValueFromValue(value, props.step)

    const prevPosition = valueTransformer.getPositionFromEvent(
      prevEvent,
      getTrackClientRect()
    )
    const prevValue = valueTransformer.getValueFromPosition(
      prevPosition,
      minValue,
      maxValue,
      getTrackClientRect()
    )
    const prevStepValue = valueTransformer.getStepValueFromValue(
      prevValue,
      step
    )

    const offset = prevStepValue - stepValue

    const transformedValues = {
      min: min - offset,
      max: max - offset
    }

    updateValues(transformedValues)
  }

  let minLabel = isMultiValue && !showSliderLabel ? value.min : minValue
  let maxLabel = isMultiValue && !showSliderLabel ? value.max : maxValue

  return (
    <StyledInputRange
      ref={node}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      data-testid="input-range"
    >
      <Label
        formatLabel={formatLabel}
        type="min"
        showSliderLabel={showSliderLabel}
      >
        {minLabel}
      </Label>
      <Track
        ref={trackNode}
        percentages={percentages}
        onTrackDrag={handleTrackDrag}
        onTrackMouseDown={handleTrackMouseDown}
      >
        {renderSliders()}
      </Track>
      <Label
        formatLabel={formatLabel}
        type="max"
        showSliderLabel={showSliderLabel}
      >
        {maxLabel}
      </Label>
    </StyledInputRange>
  )
}

export default InputRange

InputRange.defaultProps = {
  minValue: 0,
  maxValue: 10,
  step: 1,
  disabled: false,
  allowSameValues: false,
  showSliderLabel: true
}

InputRange.propTypes = {
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  step: PropTypes.number.isRequired
}
